/**
 * Onboarding Brand Select
 *
 * Step 1: User selects their brand or enters a URL manually.
 */

import { useState, useEffect } from 'react'
import { Wand2, Globe, ChevronDown, Check, AlertCircle } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import apiClient from '@/shared/lib/api'
import { useOnboarding } from '../../contexts/OnboardingContext'
import type { Brand } from '@/modules/my-company/types'

export default function OnboardingBrandSelect() {
  const { startOnboarding, state } = useOnboarding()
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null)
  const [manualUrl, setManualUrl] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Load brands on mount
  useEffect(() => {
    async function fetchBrands() {
      try {
        const response = await apiClient.get('/brands')
        setBrands(response.data.brands || [])
      } catch {
        console.warn('[OnboardingBrandSelect] Failed to load brands')
        setBrands([])
      } finally {
        setLoading(false)
      }
    }

    fetchBrands()
  }, [])

  const selectedBrand = brands.find(b => b.id === selectedBrandId)
  const urlToSubmit = selectedBrand?.url || manualUrl

  const handleSubmit = async () => {
    if (!urlToSubmit.trim()) return

    setIsSubmitting(true)
    try {
      await startOnboarding(urlToSubmit.trim())
    } catch {
      setIsSubmitting(false)
    }
  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`)
      return true
    } catch {
      return false
    }
  }

  const canSubmit = urlToSubmit.trim() && isValidUrl(urlToSubmit) && !isSubmitting

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Wand2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-tf-text mb-3">
            Let's Create Your First Ad
          </h1>
          <p className="text-tf-text-muted text-lg">
            We'll analyze your brand and create a winning ad in minutes
          </p>
        </div>

        {/* Selection Card */}
        <div className="bg-tf-bg-card rounded-2xl border border-tf-border p-8 shadow-sm">
          {/* Brand Dropdown or Manual Input */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-tf-border border-t-primary rounded-full animate-spin" />
            </div>
          ) : brands.length > 0 ? (
            <div className="space-y-6">
              {/* Brand Dropdown */}
              <div>
                <label className="block text-sm font-medium text-tf-text mb-2">
                  Select your brand
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left',
                      'bg-tf-bg-muted border-tf-border hover:border-tf-border-hover',
                      isDropdownOpen && 'border-primary ring-2 ring-primary/20'
                    )}
                  >
                    {selectedBrand ? (
                      <>
                        {selectedBrand.logoUrl ? (
                          <img
                            src={selectedBrand.logoUrl}
                            alt={selectedBrand.name}
                            className="w-8 h-8 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-tf-bg-hover flex items-center justify-center">
                            <Globe className="w-4 h-4 text-tf-text-muted" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-tf-text truncate">{selectedBrand.name}</p>
                          <p className="text-xs text-tf-text-muted truncate">{selectedBrand.url}</p>
                        </div>
                      </>
                    ) : (
                      <span className="flex-1 text-tf-text-muted">Choose a brand...</span>
                    )}
                    <ChevronDown className={cn(
                      'w-5 h-5 text-tf-text-muted transition-transform',
                      isDropdownOpen && 'rotate-180'
                    )} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-2 bg-tf-bg-card border border-tf-border rounded-xl shadow-lg overflow-hidden">
                      {brands.filter(b => b.url).map(brand => (
                        <button
                          key={brand.id}
                          type="button"
                          onClick={() => {
                            setSelectedBrandId(brand.id)
                            setManualUrl('')
                            setIsDropdownOpen(false)
                          }}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-3 hover:bg-tf-bg-hover transition-colors text-left',
                            selectedBrandId === brand.id && 'bg-primary/5'
                          )}
                        >
                          {brand.logoUrl ? (
                            <img
                              src={brand.logoUrl}
                              alt={brand.name}
                              className="w-8 h-8 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-tf-bg-hover flex items-center justify-center">
                              <Globe className="w-4 h-4 text-tf-text-muted" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-tf-text truncate">{brand.name}</p>
                            <p className="text-xs text-tf-text-muted truncate">{brand.url}</p>
                          </div>
                          {selectedBrandId === brand.id && (
                            <Check className="w-5 h-5 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-tf-border" />
                <span className="text-sm text-tf-text-muted">or</span>
                <div className="flex-1 h-px bg-tf-border" />
              </div>

              {/* Manual URL Input */}
              <div>
                <label className="block text-sm font-medium text-tf-text mb-2">
                  Enter a store URL
                </label>
                <input
                  type="url"
                  value={manualUrl}
                  onChange={(e) => {
                    setManualUrl(e.target.value)
                    setSelectedBrandId(null)
                  }}
                  placeholder="https://your-store.myshopify.com"
                  className={cn(
                    'w-full px-4 py-3 rounded-xl border transition-all',
                    'bg-tf-bg-muted border-tf-border',
                    'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
                    'placeholder:text-tf-text-muted'
                  )}
                />
              </div>
            </div>
          ) : (
            /* No brands - show only manual input */
            <div>
              <label className="block text-sm font-medium text-tf-text mb-2">
                Enter your store URL
              </label>
              <input
                type="url"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                placeholder="https://your-store.myshopify.com"
                className={cn(
                  'w-full px-4 py-3 rounded-xl border transition-all',
                  'bg-tf-bg-muted border-tf-border',
                  'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
                  'placeholder:text-tf-text-muted'
                )}
              />
            </div>
          )}

          {/* Error Message */}
          {state.error && (
            <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-500">{state.error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              'w-full mt-8 py-4 px-6 rounded-xl font-semibold text-white transition-all',
              'flex items-center justify-center gap-2',
              canSubmit
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                : 'bg-tf-bg-muted text-tf-text-muted cursor-not-allowed'
            )}
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Generate My First Ad
              </>
            )}
          </button>
        </div>

        {/* Footer hint */}
        <p className="text-center text-sm text-tf-text-muted mt-6">
          We'll analyze your brand, competitors, and create a high-converting ad
        </p>
      </div>
    </div>
  )
}
